import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AiGeneratedFlag,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfidenceBadge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  AnswerProposal,
  AnswerValue,
  AssessmentAnswer,
  AssessmentDetail,
  AssessmentQuestion,
  VendorEvidence,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { vendorApi } from "../../lib/api";
import { VendorDemoBanner } from "../../components/VendorMode";
import {
  ASSESSMENT_STATUS_LABEL,
  DECISION_LABEL,
  OUTCOME_LABEL,
  assessmentStatusTone,
  isRefusal,
} from "./labels";
import { message, usePeople } from "./helpers";

function answerText(value: AnswerValue | null): string {
  if (value === null) return "No answer";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  return value;
}

export function VenAssessmentDetail() {
  const { assessmentId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const people = usePeople(orgId);
  const [dialog, setDialog] = useState<null | "assign" | "cancel">(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const state = useAsync(async () => {
    const [assessment, proposals] = await Promise.all([
      vendorApi.assessment(orgId, assessmentId),
      vendorApi.proposals(orgId, assessmentId),
    ]);
    return { assessment, proposals };
  }, [orgId, assessmentId]);

  const assessment = state.data?.assessment.data.assessment;
  const proposals = state.data?.proposals.data.proposals ?? [];
  const demoMode = state.data?.assessment.demoMode ?? false;

  const evidence = useAsync(
    async () =>
      assessment ? (await vendorApi.vendorEvidence(orgId, assessment.vendorId)).data.evidence : [],
    [orgId, assessment?.vendorId ?? ""],
  );

  if (state.loading && !state.data) return <LoadingState label="Loading this assessment…" />;
  if (state.error) return <ErrorState description={state.error.message} onRetry={state.reload} />;
  if (!assessment) return <EmptyState title="Assessment not found" description="It may have been removed." />;

  const open = assessment.status === "OPEN";

  async function submit() {
    setError(null);
    setNotice(null);
    try {
      await vendorApi.submitAssessment(orgId, assessmentId);
      setNotice("Submitted. The answers are now the vendor's position of record.");
      state.reload();
    } catch (err) {
      setError(message(err, "Could not submit this assessment."));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p style={{ margin: 0 }}>
            <Link to="/studio/vendor-ai/assessments">Assessments</Link> ·{" "}
            <Link to={`/studio/vendor-ai/vendors/${assessment.vendorId}`}>Open the vendor</Link>
          </p>
          <h1>{assessment.title}</h1>
          <p>
            <Badge tone={assessmentStatusTone(assessment.status)}>
              {ASSESSMENT_STATUS_LABEL[assessment.status]}
            </Badge>{" "}
            Answered by {people.name(assessment.assignedTo)} · due{" "}
            {assessment.dueOn ? formatDate(assessment.dueOn) : "no date set"} · opened by{" "}
            {people.name(assessment.openedBy)} on {formatDate(assessment.openedAt)}
          </p>
        </div>
        {open && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
            <Button variant="secondary" onClick={() => setDialog("assign")}>
              Reassign
            </Button>
            <Button variant="secondary" onClick={() => setDialog("cancel")}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={assessment.requiredOutstanding > 0}>
              Submit
            </Button>
          </div>
        )}
      </div>

      <VendorDemoBanner show={demoMode} />
      {notice && <p className="ods-failclosed">{notice}</p>}
      {error && <p className="ods-failclosed">{error}</p>}

      {open && assessment.requiredOutstanding > 0 && (
        <p className="ods-failclosed">
          <strong>{assessment.requiredOutstanding} required question(s) have no answer.</strong> Submission
          stays refused until they do, so a submitted assessment always means somebody answered all of it.
        </p>
      )}
      {assessment.status === "SUBMITTED" && (
        <p className="ods-failclosed">
          Submitted by {people.name(assessment.submittedBy)} on {formatDate(assessment.submittedAt)}. Answers
          are closed.
        </p>
      )}
      {assessment.status === "CANCELLED" && (
        <p className="ods-failclosed">
          Cancelled by {people.name(assessment.cancelledBy)} on {formatDate(assessment.cancelledAt)}.{" "}
          {assessment.cancellationReason}
        </p>
      )}

      {assessment.sections.map((section) => (
        <Card key={section.id}>
          <CardHeader title={section.title} />
          <CardBody>
            {section.guidance && <p>{section.guidance}</p>}
            {section.questions.map((question) => (
              <QuestionRow
                key={question.id}
                orgId={orgId}
                assessment={assessment}
                question={question}
                answer={assessment.answers.find((a) => a.questionId === question.id) ?? null}
                proposals={proposals.filter((p) => p.questionId === question.id)}
                evidence={evidence.data ?? []}
                editable={open}
                personName={people.name}
                onChanged={state.reload}
              />
            ))}
          </CardBody>
        </Card>
      ))}

      <AssignDialog
        open={dialog === "assign"}
        orgId={orgId}
        assessment={assessment}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          state.reload();
        }}
      />
      <CancelDialog
        open={dialog === "cancel"}
        orgId={orgId}
        assessmentId={assessmentId}
        onClose={() => setDialog(null)}
        onCancelled={() => {
          setDialog(null);
          state.reload();
        }}
      />
    </>
  );
}

function QuestionRow({
  orgId,
  assessment,
  question,
  answer,
  proposals,
  evidence,
  editable,
  personName,
  onChanged,
}: {
  orgId: string;
  assessment: AssessmentDetail;
  question: AssessmentQuestion;
  answer: AssessmentAnswer | null;
  proposals: AnswerProposal[];
  evidence: VendorEvidence[];
  editable: boolean;
  personName: (id: string | null | undefined) => string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = proposals.find((p) => p.decision === "PENDING") ?? null;
  const decided = proposals.filter((p) => p.decision !== "PENDING");

  async function draft() {
    setDrafting(true);
    setError(null);
    try {
      await vendorApi.proposeAnswer(orgId, assessment.id, question.id);
      onChanged();
    } catch (err) {
      setError(message(err, "Could not draft an answer for this question."));
    } finally {
      setDrafting(false);
    }
  }

  return (
    <section
      style={{
        borderTop: "1px solid var(--ods-color-border)",
        paddingTop: "1rem",
        marginTop: "1rem",
      }}
    >
      <h3 style={{ marginBottom: "0.25rem" }}>
        {question.prompt}{" "}
        {question.required ? <Badge tone="info">Required</Badge> : <Badge>Optional</Badge>}
      </h3>
      {question.guidance && <p style={{ color: "var(--ods-color-muted)" }}>{question.guidance}</p>}
      {question.controlReference && (
        <p style={{ color: "var(--ods-color-muted)" }}>Control reference {question.controlReference}</p>
      )}

      {error && <p className="ods-failclosed">{error}</p>}

      {answer && !editing ? (
        <p>
          <strong>{answerText(answer.value)}</strong>
          <br />
          Answered by {personName(answer.answeredBy)} on {formatDate(answer.answeredAt)}
          {answer.revision > 1 ? ` · revision ${answer.revision}` : ""}
          {answer.rationale ? ` · ${answer.rationale}` : ""}
        </p>
      ) : null}
      {!answer && !editing && <p style={{ color: "var(--ods-color-muted)" }}>Not answered yet.</p>}

      {editable && !editing && (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            {answer ? "Change the answer" : "Answer"}
          </Button>
          {!pending && (
            <Button size="sm" variant="secondary" onClick={draft} disabled={drafting}>
              {drafting ? "Drafting…" : "Draft with AI"}
            </Button>
          )}
        </div>
      )}

      {editing && (
        <AnswerForm
          orgId={orgId}
          assessmentId={assessment.id}
          question={question}
          answer={answer}
          evidence={evidence}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}

      {pending && (
        <ProposalPanel
          orgId={orgId}
          assessmentId={assessment.id}
          proposal={pending}
          question={question}
          editable={editable}
          onDecided={onChanged}
        />
      )}

      {decided.map((p) => (
        <p key={p.id} style={{ color: "var(--ods-color-muted)" }}>
          <AiGeneratedFlag>Earlier draft</AiGeneratedFlag> {DECISION_LABEL[p.decision]} by{" "}
          {personName(p.decidedBy)} on {formatDate(p.decidedAt)}
          {p.decisionNote ? ` · ${p.decisionNote}` : ""}
        </p>
      ))}
    </section>
  );
}

/**
 * A proposal is the model's, not the organization's. It is shown as a draft
 * with its evidence attached, it says which model produced it, and it becomes
 * an answer only when a named person accepts it — at which point the answer is
 * theirs. A proposal that declined to answer carries no value at all and offers
 * no accept control, because there is nothing to accept.
 */
function ProposalPanel({
  orgId,
  assessmentId,
  proposal,
  question,
  editable,
  onDecided,
}: {
  orgId: string;
  assessmentId: string;
  proposal: AnswerProposal;
  question: AssessmentQuestion;
  editable: boolean;
  onDecided: () => void;
}) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refused = isRefusal(proposal.outcome);

  async function decide(decision: "ACCEPTED" | "REJECTED") {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.decideProposal(orgId, assessmentId, proposal.id, { decision, note: note.trim() });
      onDecided();
    } catch (err) {
      setError(message(err, "Could not record this decision."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ods-failclosed" style={{ borderColor: "var(--ods-color-border)" }}>
      <p style={{ marginTop: 0 }}>
        <AiGeneratedFlag>Suggested by {proposal.model}</AiGeneratedFlag>{" "}
        <Badge tone={refused ? "warning" : "info"}>{OUTCOME_LABEL[proposal.outcome]}</Badge>{" "}
        {!refused && <ConfidenceBadge value={proposal.confidence} />}
      </p>
      <p style={{ color: "var(--ods-color-muted)" }}>
        A suggestion, not an answer. Nothing here is the organization&apos;s position until a person accepts
        it,
        and the answer is then recorded under their name.
      </p>

      {refused ? (
        <p>
          <strong>{proposal.refusalReason}</strong> Answer it yourself or reject the draft. It cannot be
          accepted, because it contains no answer.
        </p>
      ) : (
        <>
          <p>
            <strong>{answerText(proposal.value)}</strong>
          </p>
          {proposal.rationale && <p>{proposal.rationale}</p>}
        </>
      )}

      {proposal.citations.length > 0 ? (
        <ul>
          {proposal.citations.map((c) => (
            <li key={`${c.evidenceId}-${c.documentVersion}`}>
              <q>{c.excerpt}</q>
              <br />
              <span style={{ color: "var(--ods-color-muted)" }}>
                Evidence {c.evidenceId} · document version {c.documentVersion} · checksum{" "}
                {c.documentChecksum.slice(0, 23)}…
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ color: "var(--ods-color-muted)" }}>No passage supports this draft.</p>
      )}

      {error && <p role="alert">{error}</p>}

      {editable && (
        <>
          <Field label="Note" help="Kept with the decision. Say what you checked.">
            {(id) => <TextInput id={id} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {!refused && (
              <Button size="sm" onClick={() => decide("ACCEPTED")} disabled={saving}>
                Accept as my answer
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => decide("REJECTED")} disabled={saving}>
              Reject
            </Button>
            <span style={{ color: "var(--ods-color-muted)", alignSelf: "center" }}>
              {refused
                ? "Or answer the question yourself above."
                : `To change it, answer ${question.prompt.slice(0, 24)}… yourself instead.`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function AnswerForm({
  orgId,
  assessmentId,
  question,
  answer,
  evidence,
  onClose,
  onSaved,
}: {
  orgId: string;
  assessmentId: string;
  question: AssessmentQuestion;
  answer: AssessmentAnswer | null;
  evidence: VendorEvidence[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial: AnswerValue =
    answer?.value ??
    (question.questionType === "BOOLEAN" ? false : question.questionType === "MULTI_SELECT" ? [] : "");
  const [value, setValue] = useState<AnswerValue>(initial);
  const [rationale, setRationale] = useState(answer?.rationale ?? "");
  const [evidenceId, setEvidenceId] = useState(answer?.evidenceId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.answerQuestion(orgId, assessmentId, question.id, {
        value,
        rationale: rationale.trim(),
        evidenceId: evidenceId === "" ? null : evidenceId,
      });
      onSaved();
    } catch (err) {
      setError(message(err, "Could not save this answer."));
    } finally {
      setSaving(false);
    }
  }

  function toggle(option: string) {
    const current = Array.isArray(value) ? value : [];
    setValue(current.includes(option) ? current.filter((o) => o !== option) : [...current, option]);
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      {error && <p className="ods-failclosed">{error}</p>}
      {question.questionType === "BOOLEAN" && (
        <Field label="Answer">
          {(id) => (
            <Select id={id} value={value === true ? "yes" : "no"} onChange={(e) => setValue(e.target.value === "yes")}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          )}
        </Field>
      )}
      {question.questionType === "SINGLE_SELECT" && (
        <Field label="Answer">
          {(id) => (
            <Select id={id} value={typeof value === "string" ? value : ""} onChange={(e) => setValue(e.target.value)}>
              <option value="">Choose one</option>
              {question.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {question.questionType === "MULTI_SELECT" && (
        <fieldset>
          <legend>Answer</legend>
          {question.options.map((o) => (
            <label key={o} style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(o)}
                onChange={() => toggle(o)}
              />{" "}
              {o}
            </label>
          ))}
        </fieldset>
      )}
      {question.questionType === "TEXT" && (
        <Field label="Answer">
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
        </Field>
      )}
      {question.questionType === "EVIDENCE_REQUEST" && (
        <Field
          label="Evidence"
          help="Evidence already attached to this vendor. Attach it on the vendor first if it is missing."
        >
          {(id) => (
            <Select
              id={id}
              value={typeof value === "string" ? value : ""}
              onChange={(e) => {
                setValue(e.target.value);
                setEvidenceId(e.target.value);
              }}
            >
              <option value="">Choose evidence</option>
              {evidence.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.evidenceTypeLabel} · {ev.documentFilename}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <Field label="Why" help="What you relied on. Kept with the answer.">
        {(id) => <TextInput id={id} value={rationale} onChange={(e) => setRationale(e.target.value)} />}
      </Field>

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save answer"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function AssignDialog({
  open,
  orgId,
  assessment,
  onClose,
  onSaved,
}: {
  open: boolean;
  orgId: string;
  assessment: AssessmentDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const people = usePeople(orgId);
  const [assignedTo, setAssignedTo] = useState(assessment.assignedTo ?? "");
  const [dueOn, setDueOn] = useState(assessment.dueOn ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.assignAssessment(orgId, assessment.id, {
        assignedTo: assignedTo === "" ? null : assignedTo,
        dueOn: dueOn === "" ? null : dueOn,
      });
      onSaved();
    } catch (err) {
      setError(message(err, "Could not change the assignment."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Who answers this, and by when"
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      {error && <p className="ods-failclosed">{error}</p>}
      <Field label="Answering">
        {(id) => (
          <Select id={id} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">Nobody</option>
            {people.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName || m.email}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Due" help="Vendor AI is the authority for this date.">
        {(id) => <TextInput id={id} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

function CancelDialog({
  open,
  orgId,
  assessmentId,
  onClose,
  onCancelled,
}: {
  open: boolean;
  orgId: string;
  assessmentId: string;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function cancel() {
    setSaving(true);
    setError(null);
    try {
      await vendorApi.cancelAssessment(orgId, assessmentId, reason.trim());
      onCancelled();
    } catch (err) {
      setError(message(err, "Could not cancel this assessment."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Cancel this assessment"
      description="The answers already given stay on the record. Cancelling only stops the assessment."
      onClose={onClose}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Keep it open
          </Button>
          <Button variant="danger" onClick={cancel} disabled={saving || reason.trim() === ""}>
            {saving ? "Cancelling…" : "Cancel the assessment"}
          </Button>
        </>
      }
    >
      {error && <p className="ods-failclosed">{error}</p>}
      <Field label="Why">
        {(id) => <TextArea id={id} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
